import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import React from "react";

import { EquipmentType } from "domain/entities/equipment-type";
import { PendingMovementPhoto } from "./useEquipmentMovementFlow";

interface EquipmentMovementDialogProps {
  visible: boolean;
  movementType: "DELIVERY" | "RETURN" | "MAINTENANCE" | "ABATE";
  movementEmployeeId: string | null;
  movementJustification: string;
  movementOdometerKm: number | null;
  movementPhotos: string[];
  movementPendingPhotos: PendingMovementPhoto[];
  uploadingPhotos: boolean;
  selectedType?: EquipmentType;
  employees: Array<{ id?: string; name?: string }>;
  dropdownLabelStyle: React.CSSProperties;
  movementPhotosInputRef: React.RefObject<HTMLInputElement | null>;
  onHide: () => void;
  onSave: () => void;
  onMovementTypeChange: (
    movementType: "DELIVERY" | "RETURN" | "MAINTENANCE" | "ABATE",
  ) => void;
  onMovementEmployeeChange: (employeeId: string | null) => void;
  onMovementJustificationChange: (value: string) => void;
  onMovementOdometerKmChange: (value: number | null) => void;
  onAddMovementPhotos: (files: File[]) => void;
  onPreviewPendingPhoto: (photo: PendingMovementPhoto) => void;
  onRemovePendingMovementPhoto: (photoId: string) => void;
}

const EquipmentMovementDialog: React.FC<EquipmentMovementDialogProps> = ({
  visible,
  movementType,
  movementEmployeeId,
  movementJustification,
  movementOdometerKm,
  movementPhotos,
  movementPendingPhotos,
  uploadingPhotos,
  selectedType,
  employees,
  dropdownLabelStyle,
  movementPhotosInputRef,
  onHide,
  onSave,
  onMovementTypeChange,
  onMovementEmployeeChange,
  onMovementJustificationChange,
  onMovementOdometerKmChange,
  onAddMovementPhotos,
  onPreviewPendingPhoto,
  onRemovePendingMovementPhoto,
}) => {
  const isVehicle = selectedType?.name.trim().toLowerCase() === "viatura";

  return (
    <Dialog
      header="Movimento de Equipamento"
      visible={visible}
      style={{ width: "40vw" }}
      contentStyle={{ height: "65vh" }}
      onHide={onHide}
      footer={
        <div>
          <Button
            label="Cancelar"
            icon="pi pi-times"
            onClick={onHide}
            className="p-button-text"
          />
          <Button
            label="Guardar"
            icon="pi pi-check"
            onClick={onSave}
            autoFocus
            disabled={uploadingPhotos}
          />
        </div>
      }
    >
      <div className="flex flex-column gap-4 mt-3" style={{ height: "100%" }}>
        <div className="field">
          <span className="p-float-label">
            <Dropdown
              id="movType"
              value={movementType}
              options={[
                { value: "DELIVERY", label: "Atribuir a Colaborador" },
                { value: "RETURN", label: "Devolução de Equipamento" },
                { value: "MAINTENANCE", label: "Envio a manutenção" },
                { value: "ABATE", label: "Abate de Equipamento" },
              ]}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => onMovementTypeChange(e.value)}
              className="w-full"
            />
            <label htmlFor="movType" style={dropdownLabelStyle}>
              Tipo de Movimento
            </label>
          </span>
        </div>

        {movementType === "DELIVERY" && (
          <div className="field">
            <span className="p-float-label">
              <Dropdown
                id="movEmployee"
                value={movementEmployeeId}
                options={employees}
                optionLabel="name"
                optionValue="id"
                onChange={(e) => onMovementEmployeeChange(e.value)}
                placeholder="Selecione o colaborador"
                className="w-full"
                filter
              />
              <label htmlFor="movEmployee" style={dropdownLabelStyle}>
                Colaborador Destinatário
              </label>
            </span>
          </div>
        )}

        {isVehicle && (
          <div className="field">
            <span className="p-float-label">
              <InputNumber
                id="movOdometerKm"
                value={movementOdometerKm}
                onValueChange={(event) =>
                  onMovementOdometerKmChange(event.value ?? null)
                }
                min={0}
                minFractionDigits={0}
                maxFractionDigits={2}
                className="w-full"
                inputClassName="w-full"
                suffix=" km"
              />
              <label htmlFor="movOdometerKm" style={dropdownLabelStyle}>
                Quilometragem atual da viatura (km) *
              </label>
            </span>
          </div>
        )}

        <div className="field">
          <span className="p-float-label">
            <InputTextarea
              id="movJust"
              value={movementJustification}
              onChange={(e) => onMovementJustificationChange(e.target.value)}
              rows={3}
              className="w-full"
              placeholder={
                movementType === "ABATE"
                  ? "Explique o motivo do abate (ex: roubo, avaria sem reparação)..."
                  : movementType === "MAINTENANCE"
                    ? "Explique o motivo do envio a manutenção..."
                    : "Notas adicionais..."
              }
            />
            <label htmlFor="movJust" style={dropdownLabelStyle}>
              Justificação / Notas
            </label>
          </span>
        </div>

        <div className="field">
          <div className="flex justify-content-between align-items-center mb-1">
            <label className="font-bold m-0">
              Fotos do Equipamento
              {movementType === "DELIVERY" && selectedType?.minDeliveryPhotos
                ? ` (Mínimo ${selectedType.minDeliveryPhotos} obrigatórias)`
                : ""}
              {movementType === "RETURN" && selectedType?.minReturnPhotos
                ? ` (Mínimo ${selectedType.minReturnPhotos} obrigatórias)`
                : ""}
            </label>
            <Button
              type="button"
              label="Adicionar Foto(s)"
              icon="pi pi-plus"
              className="p-button-outlined"
              onClick={() => movementPhotosInputRef.current?.click()}
              disabled={uploadingPhotos}
            />
          </div>
          {selectedType?.photoInstructions && (
            <small className="block text-gray-500 mb-2 italic">
              Instruções: {selectedType.photoInstructions}
            </small>
          )}
          <input
            ref={movementPhotosInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={(event) => {
              onAddMovementPhotos(Array.from(event.target.files || []));

              if (movementPhotosInputRef.current) {
                movementPhotosInputRef.current.value = "";
              }
            }}
          />

          {movementPendingPhotos.length > 0 && (
            <DataTable
              value={movementPendingPhotos}
              size="small"
              className="mt-2"
              scrollable
              scrollHeight="180px"
              tableStyle={{ fontSize: "0.85rem" }}
            >
              <Column
                field="fileName"
                header="Ficheiro"
                bodyStyle={{
                  paddingTop: "0.35rem",
                  paddingBottom: "0.35rem",
                }}
              />
              <Column
                header="Visualizar"
                body={(row: PendingMovementPhoto) => (
                  <Button
                    type="button"
                    icon="pi pi-eye"
                    severity="secondary"
                    className="p-button-rounded p-button-text p-button-sm"
                    onClick={() => onPreviewPendingPhoto(row)}
                  />
                )}
                style={{ width: "7rem" }}
                bodyStyle={{ paddingTop: "0.2rem", paddingBottom: "0.2rem" }}
              />
              <Column
                header="Eliminar"
                body={(row: PendingMovementPhoto) => (
                  <Button
                    type="button"
                    icon="pi pi-trash"
                    severity="secondary"
                    className="p-button-rounded p-button-text p-button-sm"
                    onClick={() => onRemovePendingMovementPhoto(row.id)}
                  />
                )}
                style={{ width: "7rem" }}
                bodyStyle={{ paddingTop: "0.2rem", paddingBottom: "0.2rem" }}
              />
            </DataTable>
          )}

          {movementPhotos.length > 0 && (
            <small className="block mt-2 text-gray-600">
              {movementPhotos.length} foto(s) já carregada(s) para este
              movimento.
            </small>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default EquipmentMovementDialog;
